import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { Team, Player, Formation, Position } from '../types';
import { formations } from '../components/formation/formationData';
import { supabaseTeamService } from '../services/supabaseTeamService';
import { secureTeamService } from '../services/secureTeamService';

interface Notification {
  id: string;
  message: string;
  type: 'error' | 'success' | 'warning';
  timestamp: number;
}

interface TeamState {
  team: Team;
  selectedPosition: string | null;
  fieldOrientation: 'landscape' | 'portrait';
  notifications: Notification[];
  isLoading: boolean;
  saveError: string | null;
  hasUnsavedChanges: boolean;
  lastSaved: Date | null;
}

// Load team from localStorage if available
const loadTeamFromStorage = (): TeamState => {
  if (typeof window === 'undefined') {
    return getDefaultTeamState();
  }

  try {
    const savedState = localStorage.getItem('dream11-team-state');
    if (savedState) {
      const parsedState = JSON.parse(savedState);
      // Always start with empty notifications - don't persist notifications between sessions
      parsedState.notifications = [];
      // Always start with no unsaved changes when loading from localStorage
      parsedState.hasUnsavedChanges = false;
      parsedState.lastSaved = null; // Reset last saved time on page load
      return parsedState;
    }
  } catch (error) {
    console.error('Failed to load team state from localStorage:', error);
  }

  return getDefaultTeamState();
};

// Get default team state
const getDefaultTeamState = (): TeamState => ({
  team: {
    name: 'My Dream Team',
    formation: formations[0], // Default 4-4-2
    players: {}, // Initialize as empty object instead of array
    substitutes: [],
  },
  selectedPosition: null,
  fieldOrientation: 'landscape',
  notifications: [],
  isLoading: false,
  saveError: null,
  hasUnsavedChanges: false,
  lastSaved: null,
});

const initialState: TeamState = loadTeamFromStorage();

// Helper function to save state to localStorage
const saveTeamState = (state: TeamState) => {
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem('dream11-team-state', JSON.stringify(state));
    } catch (error) {
      console.error('Failed to save team state to localStorage:', error);
    }
  }
};

// Maximum number of substitutes allowed
const MAX_SUBSTITUTES = 7;

// Async thunks for Supabase operations
export const saveTeamToSupabase = createAsyncThunk(
  'team/saveToSupabase',
  async ({ userId, team, isSupabaseUser }: { userId: string; team: Team; isSupabaseUser?: boolean }, { rejectWithValue }) => {
    try {
      console.log('💾 Starting saveTeamToSupabase:', {
        userId,
        teamName: team.name,
        playersCount: Object.keys(team.players).length,
        substitutesCount: team.substitutes.length,
        substitutes: team.substitutes
      });
      
      // Use secure API endpoints instead of direct Supabase calls
      const result = await secureTeamService.saveTeam(team);
      
      console.log('💾 Save result:', result);
      
      return result;
    } catch (error) {
      console.error('💾 Error saving team:', error);
      return rejectWithValue({ 
        message: error instanceof Error ? error.message : 'Failed to save team',
        fallback: true 
      });
    }
  }
);

export const loadTeamFromSupabase = createAsyncThunk(
  'team/loadFromSupabase',
  async ({ userId, isSupabaseUser }: { userId: string; isSupabaseUser?: boolean }, { rejectWithValue }) => {
    try {
      // Use secure API endpoints instead of direct Supabase calls
      const result = await secureTeamService.loadTeams();
      
      console.log('🔍 Load teams result:', {
        success: result.success,
        fallback: result.fallback,
        teamsCount: result.teams.length,
        firstTeam: result.teams[0] ? {
          id: result.teams[0].id,
          name: result.teams[0].name,
          team_players_count: result.teams[0].team_players?.length,
          team_substitutes_count: result.teams[0].team_substitutes?.length,
          team_substitutes: result.teams[0].team_substitutes
        } : null
      });
      
      if (result.success && result.teams.length > 0) {
        // Convert the first team to app format if it's from Supabase
        if (!result.fallback) {
          const supabaseTeam = result.teams[0];
          console.log('🔄 Converting Supabase team to app format');
          const team = supabaseTeamService.convertToAppTeam(supabaseTeam, formations);
          console.log('✅ Converted team:', {
            name: team.name,
            playersCount: Object.keys(team.players).length,
            substitutesCount: team.substitutes.length,
            substitutes: team.substitutes
          });
          return { team, fallback: false };
        } else {
          // Already in app format from localStorage
          console.log('📄 Using localStorage team format');
          return { team: result.teams[0], fallback: true };
        }
      } else {
        // No teams found, return default team
        console.log('🔄 No teams found, using fallback');
        const team = await supabaseTeamService.getTeamFallback();
        return { team, fallback: true };
      }
    } catch (error) {
      console.error('Error loading team:', error);
      // Try localStorage fallback
      const team = await supabaseTeamService.getTeamFallback();
      return rejectWithValue({ 
        message: error instanceof Error ? error.message : 'Failed to load team',
        fallback: true,
        team 
      });
    }
  }
);

export const teamSlice = createSlice({
  name: 'team',
  initialState,
  reducers: {
    setFormation: (state, action: PayloadAction<Formation>) => {
      state.team.formation = action.payload;
      state.team.players = {}; // Reset players when changing formation
      state.team.substitutes = []; // Explicitly clear substitutes when changing formation
      state.hasUnsavedChanges = true;
      saveTeamState(state);
    },
    changeFormationKeepPlayers: (state, action: PayloadAction<Formation>) => {
      const newFormation = action.payload;
      const oldPlayers = { ...state.team.players };
      
      // Get the new formation positions
      const newFormationPositions = newFormation.positions;

      // Map players to similar positions in the new formation
      let mappedPlayers: { [key: string]: Player } = {}; // Initialize as object instead of array
      
      // First try to keep players in exactly the same positions if they exist in both formations
      Object.entries(oldPlayers).forEach(([position, player]) => {
        if (player && newFormationPositions.includes(position as Position)) {
          mappedPlayers[position] = { ...player };
        }
      });
      
      // Try to map remaining players by role/area
      const remainingPositions = Object.keys(oldPlayers).filter(
        position => !Object.keys(mappedPlayers).includes(position)
      );
      
      const positionMap: Record<string, string[]> = {
        // Goalkeeper stays goalkeeper
        'GK': ['GK'],
        
        // Defenders
        'LB': ['LB', 'LWB', 'LCB'],
        'RB': ['RB', 'RWB', 'RCB'],
        'LCB': ['LCB', 'CCB', 'CB', 'LB'],
        'RCB': ['RCB', 'CCB', 'CB', 'RB'],
        'CCB': ['CCB', 'CB', 'LCB', 'RCB'],
        'CB': ['CB', 'CCB', 'LCB', 'RCB'],
        'LWB': ['LWB', 'LB', 'LM'],
        'RWB': ['RWB', 'RB', 'RM'],
        
        // Midfielders
        'CDM': ['CDM', 'CM', 'CCM', 'LCDM', 'RCDM'],
        'CM': ['CM', 'LCM', 'RCM', 'CCM', 'CDM', 'CAM'],
        'LCM': ['LCM', 'CM', 'LCDM', 'LM'],
        'RCM': ['RCM', 'CM', 'RCDM', 'RM'],
        'CCM': ['CCM', 'CM', 'CDM', 'CAM'],
        'LCDM': ['LCDM', 'CDM', 'LCM', 'LM'],
        'RCDM': ['RCDM', 'CDM', 'RCM', 'RM'],
        'LM': ['LM', 'LCM', 'LW', 'LWB'],
        'RM': ['RM', 'RCM', 'RW', 'RWB'],
        'CAM': ['CAM', 'CM', 'CF'],
        
        // Forwards
        'LW': ['LW', 'LM', 'LST'],
        'RW': ['RW', 'RM', 'RST'],
        'CF': ['CF', 'ST', 'CAM', 'LST', 'RST'],
        'ST': ['ST', 'CF', 'LST', 'RST'],
        'LST': ['LST', 'ST', 'LW', 'CF'],
        'RST': ['RST', 'ST', 'RW', 'CF']
      };
      
      // Track which positions have been filled in the new formation
      const filledPositions: Position[] = Object.keys(mappedPlayers).map(p => p as Position);
      
      // Try to map remaining players based on similar roles
      remainingPositions.forEach(position => {
        const player = oldPlayers[position as Position];
        if (!player) return; // Skip if player is null
        
        const similarPositions = positionMap[position as Position] || [];
        
        // Find the first available position in the new formation
        const newPosition = similarPositions.find(
          pos => newFormationPositions.includes(pos as Position) && !filledPositions.includes(pos as Position)
        ) as Position | undefined;
        
        if (newPosition) {
          // Add player to mapped players with new position
          filledPositions.push(newPosition);
          const playerWithNewPosition = { ...player, position: newPosition };
          mappedPlayers[newPosition] = playerWithNewPosition;
        } else if (player) {
          // If no similar position is available, move to substitutes
          const playerAsSubstitute = { ...player, isSubstitute: true };
          state.team.substitutes.push(playerAsSubstitute);
        }
      });
      
      // Update state
      state.team.formation = newFormation;
      state.team.players = mappedPlayers;
      state.hasUnsavedChanges = true;
      saveTeamState(state);
    },
    addPlayer: (state, action: PayloadAction<Player>) => {
      const player = action.payload;
      
      // Check if this player is already in the team (by ID, not position)
      const isPlayerOnField = Object.values(state.team.players).some(p => p && p.id === player.id);
      
      // Check if this player is already in substitutes
      const isPlayerInSubs = state.team.substitutes.some(p => p.id === player.id);
      
      // If the player is already in the team or substitutes, show error and don't add
      if (isPlayerOnField || isPlayerInSubs) {
        const notification: Notification = {
          id: `notification-${Date.now()}`,
          message: `${player.name} is already in your team`,
          type: 'error',
          timestamp: Date.now()
        };
        state.notifications.push(notification);
        return;
      }
      
      // Check if there's already a player in this position
      const existingPlayer = state.team.players[player.position as Position];
      
      if (existingPlayer) {
        // Move existing player to substitutes
        const playerToMove = { ...existingPlayer, isSubstitute: true } as Player;
        state.team.substitutes.push(playerToMove);
        delete state.team.players[player.position as Position];
      }
      
      // Add new player
      state.team.players[player.position as Position] = player;
      
      // Add success notification
      const notification: Notification = {
        id: `notification-${Date.now()}`,
        message: `${player.name} added to ${player.position}`,
        type: 'success',
        timestamp: Date.now()
      };
      state.notifications.push(notification);
      
      state.hasUnsavedChanges = true;
      saveTeamState(state);
    },
    addSubstitute: (state, action: PayloadAction<Player>) => {
      const player = action.payload;
      
      // Only proceed if we haven't reached the substitutes limit
      if (state.team.substitutes.length >= MAX_SUBSTITUTES) {
        const notification: Notification = {
          id: `notification-${Date.now()}`,
          message: `Cannot add more substitutes. Maximum of ${MAX_SUBSTITUTES} allowed.`,
          type: 'error',
          timestamp: Date.now()
        };
        state.notifications.push(notification);
        return;
      }
      
      // Check if this player is already in the team (by ID, not position)
      const isPlayerOnField = Object.values(state.team.players).some(p => p && p.id === player.id);
      
      // Check if this player is already in substitutes
      const isPlayerInSubs = state.team.substitutes.some(p => p.id === player.id);
      
      // If the player is already in the team or substitutes, show error and don't add
      if (isPlayerOnField || isPlayerInSubs) {
        const notification: Notification = {
          id: `notification-${Date.now()}`,
          message: `${player.name} is already in your team`,
          type: 'error',
          timestamp: Date.now()
        };
        state.notifications.push(notification);
        return;
      }
      
      // Mark as substitute and add to the list
      const substitutePlayer = { ...player, isSubstitute: true };
      state.team.substitutes.push(substitutePlayer);
      
      // Add success notification
      const notification: Notification = {
        id: `notification-${Date.now()}`,
        message: `${player.name} added as substitute`,
        type: 'success',
        timestamp: Date.now()
      };
      state.notifications.push(notification);
      
      state.hasUnsavedChanges = true;
      saveTeamState(state);
    },
    addNotification: (state, action: PayloadAction<Omit<Notification, 'id' | 'timestamp'>>) => {
      const notification: Notification = {
        id: `notification-${Date.now()}`,
        timestamp: Date.now(),
        ...action.payload
      };
      state.notifications.push(notification);
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    removePlayer: (state, action: PayloadAction<string>) => {
      const position = action.payload as Position;
      
      // Create a new players object without the specified position
      const newPlayers: {[key: string]: Player | null} = {};
      
      // Copy all players except the one at the specified position
      Object.entries(state.team.players).forEach(([pos, player]) => {
        if (pos !== position) {
          newPlayers[pos] = player;
        }
      });
      
      // Replace the players object instead of using delete
      state.team.players = newPlayers;
      
      state.hasUnsavedChanges = true;
      saveTeamState(state);
    },
    removeSubstitute: (state, action: PayloadAction<string>) => {
      const playerId = action.payload;
      state.team.substitutes = state.team.substitutes.filter(player => player.id !== playerId);
      state.hasUnsavedChanges = true;
      saveTeamState(state);
    },
    swapPlayerWithSubstitute: (state, action: PayloadAction<{ playerId: string; substituteId: string }>) => {
      const { playerId, substituteId } = action.payload;
      
      // playerId can now be either an actual ID or a position
      // First check if it's a position by looking it up directly
      const playerPosition = playerId as Position;
      const player = state.team.players[playerPosition];
      
      // Find the substitute by ID
      const substitute = state.team.substitutes.find(s => s.id === substituteId);
      
      if (player && substitute) {
        // Create player objects with proper types
        const playerAsSubstitute = { 
          ...player, 
          isSubstitute: true 
        } as Player;
        
        const substituteAsPlayer = {
          ...substitute,
          position: playerPosition,
          isSubstitute: false
        } as Player;
        
        // Update players object
        const newPlayers = { ...state.team.players };
        delete newPlayers[playerPosition];
        newPlayers[playerPosition] = substituteAsPlayer;
        state.team.players = newPlayers;
        
        // Filter out the old substitute and add the new one
        state.team.substitutes = state.team.substitutes
          .filter(s => s.id !== substituteId)
          .concat([playerAsSubstitute]);
      }
      saveTeamState(state);
    },
    setTeamName: (state, action: PayloadAction<string>) => {
      state.team.name = action.payload;
      state.hasUnsavedChanges = true;
      saveTeamState(state);
    },
    setSelectedPosition: (state, action: PayloadAction<string | null>) => {
      state.selectedPosition = action.payload;
      // Don't save selectedPosition to localStorage, it's UI state
    },
    resetTeam: (state) => {
      const newState = getDefaultTeamState();
      state.team = newState.team;
      state.team.substitutes = []; // Ensure substitutes are cleared
      state.notifications = []; // Clear all notifications when resetting team
      saveTeamState(state);
    },
    movePlayer: (state, action: PayloadAction<{ fromPosition: Position; toPosition: Position }>) => {
      const { fromPosition, toPosition } = action.payload;
      
      // Find the player at the source position
      const player = state.team.players[fromPosition];
      
      if (player) {
        // Remove player from original position
        delete state.team.players[fromPosition];
        
        // If there's a player in the target position
        if (state.team.players[toPosition]) {
          // Get the player at the target
          const targetPlayer = state.team.players[toPosition];
          
          if (targetPlayer) {
            // Update positions
            targetPlayer.position = fromPosition;
            player.position = toPosition;
            
            // Remove the target player
            delete state.team.players[toPosition];
            
            // Add both players back with swapped positions
            state.team.players[fromPosition] = targetPlayer;
            state.team.players[toPosition] = player;
          } else {
            // Just update the position and add back if targetPlayer is null
            player.position = toPosition;
            state.team.players[toPosition] = player;
          }
        } else {
          // Just update the position and add back
          player.position = toPosition;
          state.team.players[toPosition] = player;
        }
        
        state.hasUnsavedChanges = true;
        saveTeamState(state);
      }
    },
    movePlayerToSubstitutes: (state, action: PayloadAction<string>) => {
      const position = action.payload as Position;
      
      // Only proceed if we haven't reached the substitutes limit
      if (state.team.substitutes.length >= MAX_SUBSTITUTES) {
        return;
      }
      
      // Find the player at the position
      const player = state.team.players[position];
      
      if (player) {
        // Mark as substitute and convert to Player type
        const playerToMove = { 
          ...player, 
          isSubstitute: true
        } as Player;
        
        // Create a new players object without this position
        const newPlayers: {[key: string]: Player | null} = {};
        Object.entries(state.team.players).forEach(([pos, p]) => {
          if (pos !== position) {
            newPlayers[pos] = p;
          }
        });
        
        // Replace the players object
        state.team.players = newPlayers;
        
        // Add to substitutes
        state.team.substitutes.push(playerToMove);
      }
      
      state.hasUnsavedChanges = true;
      saveTeamState(state);
    },
    moveSubstituteToField: (state, action: PayloadAction<{ substituteId: string; position: Position }>) => {
      const { substituteId, position } = action.payload;
      
      // Find the substitute by ID
      const substitute = state.team.substitutes.find(s => s.id === substituteId);
      
      if (substitute) {
        // Update properties for the field
        const fieldPlayer = { 
          ...substitute, 
          position: position,
          isSubstitute: false 
        } as Player;
        
        // Remove from substitutes
        state.team.substitutes = state.team.substitutes.filter(s => s.id !== substituteId);
        
        // Add to players on the field - use new object to avoid mutation issues
        const newPlayers = { ...state.team.players };
        newPlayers[position] = fieldPlayer;
        state.team.players = newPlayers;
      }
      
      state.hasUnsavedChanges = true;
      saveTeamState(state);
    },
    importTeam: (state, action: PayloadAction<Team>) => {
      const importedTeam = action.payload;
      
      // Validate formation - ensure it exists in our formations
      const foundFormation = formations.find(f => f.id === importedTeam.formation.id);
      
      // If formation doesn't exist in our app, use the default
      const validatedFormation = foundFormation || formations[0];
      
      // Set the team with validated formation
      state.team = {
        ...importedTeam,
        formation: validatedFormation
      };
      
      state.hasUnsavedChanges = true;
      // Save to localStorage
      saveTeamState(state);
    },
    toggleFieldOrientation: (state) => {
      state.fieldOrientation = state.fieldOrientation === 'landscape' ? 'portrait' : 'landscape';
      saveTeamState(state);
    },
    clearSaveError: (state) => {
      state.saveError = null;
    },
    clearAllData: (state) => {
      // Complete reset for sign out - clear everything including notifications
      const newState = getDefaultTeamState();
      state.team = newState.team;
      state.selectedPosition = null;
      state.notifications = [];
      state.isLoading = false;
      state.saveError = null;
      state.hasUnsavedChanges = false;
      state.lastSaved = null;
      saveTeamState(state);
    },
  },
  extraReducers: (builder) => {
    builder
      // Save team to Supabase
      .addCase(saveTeamToSupabase.pending, (state) => {
        state.isLoading = true;
        state.saveError = null;
      })
      .addCase(saveTeamToSupabase.fulfilled, (state, action) => {
        state.isLoading = false;
        state.saveError = null;
        
        console.log('💾 saveTeamToSupabase.fulfilled:', {
          success: action.payload.success,
          fallback: action.payload.fallback,
          teamId: action.payload.teamId
        });
        
        // Only mark as saved if it was actually successful
        if (action.payload.success) {
          state.hasUnsavedChanges = false;
          state.lastSaved = new Date();
          
          // Persist the team ID if it was returned from the save operation
          if (action.payload.teamId && !state.team.id) {
            state.team.id = action.payload.teamId;
            saveTeamState(state); // Save the updated state with team ID
          }
          
          // Show appropriate success message
          const message = action.payload.fallback 
            ? 'Team saved locally (cloud save failed)'
            : 'Team saved to cloud successfully';
            
          const notification: Notification = {
            id: `notification-${Date.now()}`,
            message,
            type: action.payload.fallback ? 'warning' : 'success',
            timestamp: Date.now()
          };
          state.notifications.push(notification);
        } else {
          // Save failed completely
          state.saveError = 'Failed to save team';
          
          const notification: Notification = {
            id: `notification-${Date.now()}`,
            message: 'Failed to save team. Please try again.',
            type: 'error',
            timestamp: Date.now()
          };
          state.notifications.push(notification);
        }
      })
      .addCase(saveTeamToSupabase.rejected, (state, action) => {
        state.isLoading = false;
        state.saveError = action.payload as any;
        
        const notification: Notification = {
          id: `notification-${Date.now()}`,
          message: 'Failed to save team to cloud. Saved locally instead.',
          type: 'warning',
          timestamp: Date.now()
        };
        state.notifications.push(notification);
      })
      // Load team from Supabase
      .addCase(loadTeamFromSupabase.pending, (state) => {
        state.isLoading = true;
        state.saveError = null;
      })
      .addCase(loadTeamFromSupabase.fulfilled, (state, action) => {
        state.isLoading = false;
        state.saveError = null;
        
        console.log('🔍 loadTeamFromSupabase.fulfilled called with:', {
          hasTeam: !!action.payload.team,
          fallback: action.payload.fallback,
          team: action.payload.team ? {
            name: action.payload.team.name,
            playersCount: Object.keys(action.payload.team.players).length,
            substitutesCount: action.payload.team.substitutes.length,
            substitutes: action.payload.team.substitutes
          } : null
        });
        
        if (action.payload.team) {
          console.log('📝 Setting team in state. Before:', {
            currentSubstitutesCount: state.team.substitutes.length,
            currentSubstitutes: state.team.substitutes
          });
          
          state.team = action.payload.team;
          
          console.log('✅ Team set in state. After:', {
            newSubstitutesCount: state.team.substitutes.length,
            newSubstitutes: state.team.substitutes
          });
          
          state.hasUnsavedChanges = false; // Team just loaded, no unsaved changes
          state.lastSaved = new Date(); // Mark as fresh from server
          
          // Only show notification for successful cloud loads, not localStorage fallbacks
          if (!action.payload.fallback) {
            const notification: Notification = {
              id: `notification-${Date.now()}`,
              message: 'Team loaded from cloud successfully',
              type: 'success',
              timestamp: Date.now()
            };
            state.notifications.push(notification);
          }
        }
      })
      .addCase(loadTeamFromSupabase.rejected, (state, action) => {
        state.isLoading = false;
        state.saveError = action.payload as any;
        
        // If there's a fallback team, load it
        const payload = action.payload as any;
        if (payload?.team) {
          state.team = payload.team;
        }
      });
  },
});

export const {
  setFormation,
  changeFormationKeepPlayers,
  addPlayer,
  addSubstitute,
  removePlayer,
  removeSubstitute,
  swapPlayerWithSubstitute,
  setTeamName,
  setSelectedPosition,
  resetTeam,
  movePlayer,
  movePlayerToSubstitutes,
  moveSubstituteToField,
  importTeam,
  toggleFieldOrientation,
  addNotification,
  removeNotification,
  clearNotifications,
  clearSaveError,
  clearAllData,
} = teamSlice.actions;

export default teamSlice.reducer; 